import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGet, usePost } from "../../api";
import {
  IAffiliateNetworksResponse,
  ISyncConfiguration,
  ISyncConfigsResponse,
  SyncConfigStatus,
  IOperationState,
  INetworkCategories,
} from "../../interfaces";
import {
  Button,
  Spinner,
  Badge,
  Table,
  Modal,
  Form,
  OverlayTrigger,
  Tooltip,
  Alert,
  Pagination,
  Row,
  Col,
} from "react-bootstrap";
import {
  RefreshCw,
  Play,
  CheckCircle,
  XCircle,
  Settings,
  Activity,
} from "react-feather";
import { Link } from "react-router";

const statusBadge = (config: ISyncConfiguration) => {
  let modeBadge: React.ReactNode;
  if (!config.isActive) {
    modeBadge = <Badge bg="secondary">Inactive</Badge>;
  } else if (config.productIngestionEnabled) {
    modeBadge = <Badge bg="success">Products Enabled</Badge>;
  } else {
    modeBadge = <Badge bg="warning">Categories Only</Badge>;
  }

  if (!config.currentStatus) {
    return modeBadge;
  }

  const statusVariant: Record<string, { bg: string; text?: "dark" }> = {
    queued: { bg: "light", text: "dark" },
    running: { bg: "primary" },
    stalled: { bg: "warning", text: "dark" },
    failed: { bg: "danger" },
    succeeded: { bg: "info" },
    idle: { bg: "secondary" },
  };
  const variant = statusVariant[config.currentStatus] ?? { bg: "secondary" };

  return (
    <div className="d-flex align-items-center gap-1 flex-wrap">
      {modeBadge}
      <Badge bg={variant.bg} text={variant.text}>
        {config.currentStatus}
      </Badge>
    </div>
  );
};

const syncResultBadge = (config: ISyncConfiguration) => {
  if (!config.lastSyncResult) return <span className="text-muted">—</span>;
  const r = config.lastSyncResult;
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>
          +{r.inserted} / ~{r.updated} / -{r.softDeleted} / {r.unchanged}{" "}
          unchanged ({r.durationMs}ms)
        </Tooltip>
      }
    >
      <Badge bg={r.isSuccess ? "info" : "danger"}>
        {r.totalChanges} changes / {r.totalProcessed} total
      </Badge>
    </OverlayTrigger>
  );
};

export const SyncConfigs: React.FC = () => {
  const pageSize = 50;
  const [hasLoadedConfigs, setHasLoadedConfigs] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterNetwork, setFilterNetwork] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState<"" | SyncConfigStatus>("");
  const [appliedNetwork, setAppliedNetwork] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [appliedStatus, setAppliedStatus] = useState<"" | SyncConfigStatus>("");
  const offset = (currentPage - 1) * pageSize;

  const syncConfigsUrl = useMemo(() => {
    if (!hasLoadedConfigs) return null;
    const params = new URLSearchParams({
      offset: String(offset),
      limit: String(pageSize),
    });
    if (appliedNetwork) params.set("network", appliedNetwork);
    if (appliedName.trim()) params.set("name", appliedName.trim());
    if (appliedStatus) params.set("status", appliedStatus);
    return `affiliate-products/sync/configs?${params.toString()}`;
  }, [
    hasLoadedConfigs,
    offset,
    pageSize,
    appliedNetwork,
    appliedName,
    appliedStatus,
  ]);

  const configs = useGet<ISyncConfigsResponse>(
    syncConfigsUrl,
    hasLoadedConfigs
  );
  const syncStatus = useGet<IOperationState[]>(
    "affiliate-products/status/sync",
    false
  );
  const syncPost = usePost();
  const configPost = usePost();
  const networks = useGet<IAffiliateNetworksResponse>(
    "affiliate-products/networks",
    true
  );
  const networkOptions = useMemo(
    () => networks.data?.items || [],
    [networks.data]
  );

  // Suggested categories — fetch when modal is open with network + advertiserId
  const [suggestCatUrl, setSuggestCatUrl] = useState<string | null>(null);
  const suggestedCats = useGet<INetworkCategories>(
    suggestCatUrl,
    !!suggestCatUrl
  );
  const suggestedCategoryNames = useMemo(() => {
    if (!suggestedCats.data?.advertisers?.length) return [];
    return (
      suggestedCats.data.advertisers[0]?.categories?.map((c) => c.category) ??
      []
    );
  }, [suggestedCats.data]);

  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ISyncConfiguration | null>(
    null
  );
  const [modalNetwork, setModalNetwork] = useState("");
  const [modalAdvertiserId, setModalAdvertiserId] = useState("");
  const [modalCategories, setModalCategories] = useState("");

  // Build a lookup of running syncs keyed by "Network-AdvertiserId"
  const runningSyncs = new Set(
    (syncStatus.data || [])
      .filter((s) => s.isRunning)
      .map((s) => s.operationKey)
  );

  const isSyncRunning = (config: ISyncConfiguration) =>
    runningSyncs.has(`${config.affiliateNetwork}-${config.advertiserId}`);

  const totalConfigs = configs.data?.totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(totalConfigs / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage !== safeCurrentPage) {
      setCurrentPage(safeCurrentPage);
    }
  }, [currentPage, safeCurrentPage]);

  const paginationItems = useMemo(() => {
    if (totalPages <= 1) return [] as number[];
    const windowSize = 5;
    const start = Math.max(1, safeCurrentPage - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const adjustedStart = Math.max(1, end - windowSize + 1);
    return Array.from(
      { length: end - adjustedStart + 1 },
      (_, i) => adjustedStart + i
    );
  }, [safeCurrentPage, totalPages]);

  const handleRefreshAll = useCallback(async () => {
    setHasLoadedConfigs(true);
    setAppliedNetwork(filterNetwork);
    setAppliedName(filterName);
    setAppliedStatus(filterStatus);
    await Promise.all([configs.refetch(), syncStatus.refetch()]);
  }, [configs, syncStatus, filterNetwork, filterName, filterStatus]);

  const handleRunSync = useCallback(
    async (network: string, advertiserId: string) => {
      try {
        await syncPost.execute(
          `affiliate-products/sync/run/${network}/${advertiserId}`
        );
        await Promise.all([configs.refetch(), syncStatus.refetch()]);
      } catch (e: any) {
        // Refresh status on 409 to show the running indicator
        syncStatus.refetch();
        console.error("Sync failed:", e);
      }
    },
    [syncPost, configs, syncStatus]
  );

  const handleToggleProducts = useCallback(
    async (config: ISyncConfiguration) => {
      const action = config.productIngestionEnabled
        ? "disable-products"
        : "enable-products";
      try {
        await configPost.execute(
          `affiliate-products/sync/config/${config.affiliateNetwork}/${config.advertiserId}/${action}`
        );
        await configs.refetch();
      } catch (e) {
        console.error("Toggle failed:", e);
      }
    },
    [configPost, configs]
  );

  const handleCreateConfig = useCallback(async () => {
    const net = editingConfig?.affiliateNetwork || modalNetwork;
    const advId = editingConfig?.advertiserId || modalAdvertiserId;
    if (!advId.trim()) return;
    try {
      let url = `affiliate-products/sync/config/${net}/${advId}`;
      if (modalCategories.trim()) {
        url += `?categories=${encodeURIComponent(modalCategories.trim())}`;
      }
      await configPost.execute(url);
      setShowModal(false);
      setEditingConfig(null);
      setModalAdvertiserId("");
      setModalCategories("");
      await configs.refetch();
    } catch (e) {
      console.error("Save config failed:", e);
    }
  }, [
    configPost,
    configs,
    editingConfig,
    modalNetwork,
    modalAdvertiserId,
    modalCategories,
  ]);

  const handleEditConfig = useCallback((config: ISyncConfiguration) => {
    setEditingConfig(config);
    setModalNetwork(config.affiliateNetwork);
    setModalAdvertiserId(config.advertiserId);
    setModalCategories(config.categoryWhitelist?.join(", ") || "");
    setSuggestCatUrl(
      `affiliate-products/categories?network=${config.affiliateNetwork}&advertiserId=${config.advertiserId}`
    );
    setShowModal(true);
  }, []);

  const handleOpenNewConfig = useCallback(() => {
    setEditingConfig(null);
    setModalNetwork(networkOptions[0] || "");
    setModalAdvertiserId("");
    setModalCategories("");
    setSuggestCatUrl(null);
    setShowModal(true);
  }, [networkOptions]);

  const handleLoadSuggestions = useCallback(() => {
    if (modalNetwork && modalAdvertiserId.trim()) {
      setSuggestCatUrl(
        `affiliate-products/categories?network=${modalNetwork}&advertiserId=${modalAdvertiserId.trim()}`
      );
    }
  }, [modalNetwork, modalAdvertiserId]);

  const handleAddSuggestedCategory = useCallback(
    (cat: string) => {
      const current = modalCategories
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (!current.includes(cat)) {
        setModalCategories([...current, cat].join(", "));
      }
    },
    [modalCategories]
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedNetwork(filterNetwork);
    setAppliedName(filterName);
    setAppliedStatus(filterStatus);
    setCurrentPage(1);
    setHasLoadedConfigs(true);
  }, [filterNetwork, filterName, filterStatus]);

  const handleClearFilters = useCallback(() => {
    setFilterNetwork("");
    setFilterName("");
    setFilterStatus("");
    setAppliedNetwork("");
    setAppliedName("");
    setAppliedStatus("");
    setCurrentPage(1);
    setHasLoadedConfigs(true);
  }, []);

  const statusOptions: Array<{ value: "" | SyncConfigStatus; label: string }> =
    [
      { value: "", label: "All Statuses" },
      { value: "queued", label: "Queued" },
      { value: "running", label: "Running" },
      { value: "stalled", label: "Stalled" },
      { value: "failed", label: "Failed" },
      { value: "succeeded", label: "Succeeded" },
      { value: "idle", label: "Idle" },
    ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Sync Configurations</h4>
        <div>
          <Button
            size="sm"
            variant="outline-primary"
            className="me-2"
            onClick={() => {
              setCurrentPage(1);
              handleRefreshAll();
            }}
            disabled={configs.loading}
          >
            <RefreshCw size={14} className="me-1" />
            {hasLoadedConfigs ? "Refresh" : "Load Configurations"}
          </Button>
          <Button size="sm" variant="primary" onClick={handleOpenNewConfig}>
            <Settings size={14} className="me-1" />
            New Config
          </Button>
        </div>
      </div>

      <Row className="g-2 mb-3">
        <Col md={3}>
          <Form.Select
            size="sm"
            value={filterNetwork}
            onChange={(e) => setFilterNetwork(e.target.value)}
          >
            <option value="">All Networks</option>
            {networkOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Control
            size="sm"
            placeholder="Filter by advertiser name or ID"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
          />
        </Col>
        <Col md={3}>
          <Form.Select
            size="sm"
            value={filterStatus}
            onChange={(e) =>
              setFilterStatus(e.target.value as "" | SyncConfigStatus)
            }
          >
            {statusOptions.map((status) => (
              <option key={status.value || "all"} value={status.value}>
                {status.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2} className="d-flex gap-2">
          <Button size="sm" variant="primary" onClick={handleApplyFilters}>
            Apply
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleClearFilters}
          >
            Clear
          </Button>
        </Col>
      </Row>

      {runningSyncs.size > 0 && (
        <Alert variant="info" className="mb-3">
          <Activity size={14} className="me-1" />
          <strong>{runningSyncs.size}</strong> sync(s) currently running
          <Button
            size="sm"
            variant="link"
            className="p-0 ms-2"
            onClick={() => syncStatus.refetch()}
          >
            Refresh status
          </Button>
        </Alert>
      )}

      {syncPost.error && (
        <Alert
          variant={syncPost.error.status === 409 ? "warning" : "danger"}
          className="mb-3"
          dismissible
        >
          {syncPost.error.status === 409
            ? "Sync is already running for this advertiser."
            : `Sync failed: ${syncPost.error.message}`}
        </Alert>
      )}

      {!hasLoadedConfigs && (
        <Alert variant="secondary" className="mb-3">
          Click Load Configurations to fetch sync status and configs.
        </Alert>
      )}

      {hasLoadedConfigs && configs.loading && (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" /> Loading...
        </div>
      )}

      {configs.error && (
        <div className="text-danger mb-3">Error: {configs.error.message}</div>
      )}

      {hasLoadedConfigs &&
        !configs.loading &&
        configs.data &&
        (() => {
          const configList = Array.isArray(configs.data.configs)
            ? configs.data.configs
            : [];
          const pageStart = totalConfigs === 0 ? 0 : offset + 1;
          const pageEnd = Math.min(offset + configList.length, totalConfigs);
          return (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
                <small className="text-muted mb-0">
                  {`Showing ${pageStart}-${pageEnd} of ${totalConfigs} sync configurations`}
                </small>
                <small className="text-muted mb-0">Per page: 50</small>
              </div>
              <Table
                striped
                bordered
                hover
                size="sm"
                style={{ fontSize: "0.85rem" }}
              >
                <thead>
                  <tr>
                    <th>Network</th>
                    <th>Advertiser</th>
                    <th>Status</th>
                    <th>Category Whitelist</th>
                    <th>Last Sync</th>
                    <th>Last Result</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {configList.map((config) => (
                    <tr key={config.id}>
                      <td>
                        <Badge bg="dark">{config.affiliateNetwork}</Badge>
                      </td>
                      <td>
                        <strong>
                          <Link
                            to={`/affiliate-products/advertiser/${config.affiliateNetwork}/${config.advertiserId}`}
                          >
                            {config.advertiserName || config.advertiserId}
                          </Link>
                        </strong>
                        <br />
                        <small className="text-muted">
                          {config.advertiserId}
                        </small>
                      </td>
                      <td>{statusBadge(config)}</td>
                      <td>
                        {config.categoryWhitelist?.length ? (
                          <small>
                            {config.categoryWhitelist.slice(0, 3).join(", ")}
                            {config.categoryWhitelist.length > 3 &&
                              ` +${config.categoryWhitelist.length - 3} more`}
                          </small>
                        ) : (
                          <span className="text-muted">All</span>
                        )}
                      </td>
                      <td>
                        {config.lastSyncedAt ? (
                          <small>
                            {new Date(config.lastSyncedAt).toLocaleString()}
                          </small>
                        ) : (
                          <span className="text-muted">Never</span>
                        )}
                      </td>
                      <td>{syncResultBadge(config)}</td>
                      <td>
                        <Button
                          size="sm"
                          variant={
                            isSyncRunning(config)
                              ? "warning"
                              : "outline-success"
                          }
                          className="me-1"
                          title={
                            isSyncRunning(config)
                              ? "Sync is running..."
                              : "Run Sync"
                          }
                          onClick={() =>
                            handleRunSync(
                              config.affiliateNetwork,
                              config.advertiserId
                            )
                          }
                          disabled={syncPost.loading || isSyncRunning(config)}
                        >
                          {isSyncRunning(config) ? (
                            <Spinner
                              animation="border"
                              size="sm"
                              style={{ width: 12, height: 12 }}
                            />
                          ) : (
                            <Play size={12} />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant={
                            config.productIngestionEnabled
                              ? "outline-warning"
                              : "outline-primary"
                          }
                          title={
                            config.productIngestionEnabled
                              ? "Disable Products"
                              : "Enable Products"
                          }
                          onClick={() => handleToggleProducts(config)}
                          disabled={configPost.loading}
                        >
                          {config.productIngestionEnabled ? (
                            <XCircle size={12} />
                          ) : (
                            <CheckCircle size={12} />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="ms-1"
                          title="Edit Config"
                          onClick={() => handleEditConfig(config)}
                        >
                          <Settings size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {configList.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted">
                        No sync configurations found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              {totalPages > 1 && (
                <div className="d-flex justify-content-end mt-2">
                  <Pagination size="sm" className="mb-0">
                    <Pagination.Prev
                      disabled={safeCurrentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    />
                    {paginationItems.map((page) => (
                      <Pagination.Item
                        key={page}
                        active={page === safeCurrentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={safeCurrentPage >= totalPages}
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                    />
                  </Pagination>
                </div>
              )}
            </>
          );
        })()}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingConfig ? "Edit Sync Config" : "Create Sync Config"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Network</Form.Label>
              <Form.Select
                value={modalNetwork}
                onChange={(e) => setModalNetwork(e.target.value)}
                disabled={!!editingConfig}
              >
                {networkOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Advertiser ID</Form.Label>
              <Form.Control
                placeholder="e.g. 3787"
                value={modalAdvertiserId}
                onChange={(e) => setModalAdvertiserId(e.target.value)}
                disabled={!!editingConfig}
              />
              {editingConfig?.advertiserName && (
                <Form.Text className="text-muted">
                  {editingConfig.advertiserName}
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Category Whitelist (optional, comma-separated)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="e.g. Clothing, Shoes (blank = all categories)"
                value={modalCategories}
                onChange={(e) => setModalCategories(e.target.value)}
              />
              {modalAdvertiserId.trim() && !suggestedCategoryNames.length && (
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 mt-1"
                  onClick={handleLoadSuggestions}
                  disabled={suggestedCats.loading}
                >
                  {suggestedCats.loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        style={{ width: 12, height: 12 }}
                      />{" "}
                      Loading…
                    </>
                  ) : (
                    "Load suggested categories"
                  )}
                </Button>
              )}
              {suggestedCategoryNames.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted d-block mb-1">
                    Suggested ({suggestedCategoryNames.length}):
                  </small>
                  <div className="d-flex flex-wrap gap-1">
                    {suggestedCategoryNames.map((cat) => {
                      const currentList = modalCategories
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                      const isSelected = currentList.includes(cat);
                      return (
                        <Badge
                          key={cat}
                          bg={isSelected ? "primary" : "light"}
                          text={isSelected ? "white" : "dark"}
                          style={{ cursor: "pointer", fontSize: "0.75rem" }}
                          onClick={() => handleAddSuggestedCategory(cat)}
                        >
                          {isSelected ? "✓ " : "+ "}
                          {cat}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateConfig}
            disabled={
              (!editingConfig &&
                (!modalAdvertiserId.trim() || !modalNetwork.trim())) ||
              configPost.loading
            }
          >
            {configPost.loading ? (
              <Spinner animation="border" size="sm" />
            ) : editingConfig ? (
              "Save Changes"
            ) : (
              "Create"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
